/**
 * Placeholders de carga: la forma de lo que viene, mientras viene.
 *
 * PARA QUÉ SIRVEN DE VERDAD. No es decoración: es evitar que la página salte.
 * Un bloque vacío que de golpe se convierte en una tabla de doce filas mueve
 * todo lo que hay debajo, y quien ya iba a pulsar un botón termina pulsando otro.
 * El esqueleto ocupa desde el principio el espacio que va a ocupar el contenido.
 *
 * NO SUSTITUYEN A UN ESTADO CON TEXTO. Este componente es para la espera corta y
 * normal. Cuando la espera tiene una explicación —«el horario todavía no está
 * cargado», «no se pudo consultar»— eso es un `Estado` con todas sus letras, no
 * unas barras grises. Unas barras grises eternas no dicen nada y son
 * indistinguibles de una pantalla rota.
 *
 * LO QUE PARPADEA NO SE ANUNCIA. Las barras llevan `aria-hidden`: para un lector
 * de pantalla no existen. Lo que se anuncia es una frase, una sola vez, y el
 * contenedor queda marcado con `aria-busy` para que las tecnologías de apoyo
 * sepan que esa región todavía se está llenando.
 */

interface PropiedadesEsqueletoDeTabla {
  /** Cuántas columnas tendrá la tabla real. */
  columnas: number
  /** Cuántas filas se dibujan mientras llega. */
  filas?: number
  /** Qué se está cargando. Se anuncia; por eso dice el qué, no solo «cargando». */
  etiqueta: string
}

export function EsqueletoDeTabla({
  columnas,
  filas = 5,
  etiqueta,
}: PropiedadesEsqueletoDeTabla) {
  return (
    <div
      className="tabla-envoltura"
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <ESTILOS />
      <span className="visualmente-oculto">{etiqueta}</span>
      {/* La tabla es andamio visual, no datos. Anunciarla daría «tabla de 5
          filas por 4 columnas, celda vacía, celda vacía…», que es ruido puro. */}
      <table className="tabla" aria-hidden="true">
        <tbody>
          {Array.from({ length: filas }, (_, fila) => (
            <tr key={fila}>
              {Array.from({ length: columnas }, (_, columna) => (
                <td key={columna}>
                  {/* Anchos alternados: unas barras todas iguales se leen como
                      un patrón de fondo y no como texto que está por llegar. */}
                  <span
                    className="esqueleto__barra"
                    style={{ width: columna === 0 ? '60%' : '85%' }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface PropiedadesEsqueletoDeTarjeta {
  /** Renglones de texto simulados bajo el título. */
  lineas?: number
  etiqueta: string
}

export function EsqueletoDeTarjeta({
  lineas = 3,
  etiqueta,
}: PropiedadesEsqueletoDeTarjeta) {
  return (
    <div className="tarjeta" role="status" aria-busy="true" aria-live="polite">
      <ESTILOS />
      <span className="visualmente-oculto">{etiqueta}</span>
      <div aria-hidden="true">
        <span className="esqueleto__barra esqueleto__barra--titulo" />
        {Array.from({ length: lineas }, (_, linea) => (
          <span
            key={linea}
            className="esqueleto__barra"
            // El último renglón corto imita cómo termina un párrafo de verdad.
            style={{ width: linea === lineas - 1 ? '45%' : '100%' }}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Estilos del esqueleto. `precedence` los monta una sola vez por página aunque
 * haya diez esqueletos a la vez. Ningún color literal: el brillo se construye
 * con `color-mix` sobre tokens, así que el tema oscuro lo sigue solo.
 */
function ESTILOS() {
  return (
    <style href="cubiculos-esqueleto" precedence="componente">{`
      .esqueleto__barra {
        display: block;
        height: 0.75rem;
        margin: var(--esp-2) 0;
        border-radius: var(--radio-completo);
        background-color: var(--superficie-hundida);
        background-image: linear-gradient(
          90deg,
          var(--superficie-hundida) 0%,
          color-mix(in srgb, var(--superficie-hundida) 60%, var(--superficie-elevada)) 50%,
          var(--superficie-hundida) 100%
        );
        background-size: 200% 100%;
        animation: esqueleto-brillo 1.4s ease-in-out infinite;
      }

      .esqueleto__barra--titulo {
        height: 1.1rem;
        width: 40%;
        margin-bottom: var(--esp-4);
      }

      @keyframes esqueleto-brillo {
        from { background-position: 200% 0; }
        to { background-position: -200% 0; }
      }

      /* Quien pide menos movimiento recibe una barra quieta, no una barra
         invisible: sigue haciendo su trabajo de reservar el espacio. */
      @media (prefers-reduced-motion: reduce) {
        .esqueleto__barra {
          animation: none;
          background-image: none;
        }
      }
    `}</style>
  )
}
